import { TypedDocumentNode, gql } from '@apollo/client';
import DOMPurify from 'dompurify';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { BodyM, HeadlineM, LabelS } from '@sorare/core/src/atoms/typography';

import { BLOG_POST, BlogSport } from 'constants/routes';
import { usePostImage } from 'hooks/usePostImage';

import { PostExcerpt } from './__generated__/index.graphql';

const excerptFragment = gql`
  fragment PostExcerpt on Post {
    databaseId
    title
    slug
    excerpt
    content
    featured {
      featured
    }
    featuredImage {
      node {
        altText
        thumbnail: sourceUrl(size: MEDIUM)
        sourceUrl: sourceUrl(size: LARGE)
      }
    }
  }
` as TypedDocumentNode<PostExcerpt>;

interface Props {
  excerpt: PostExcerpt;
  featured: boolean;
  category: BlogSport | undefined | null;
}

const Container = styled(Link)`
  position: relative;

  & {
    text-decoration: none !important;
  }

  & figure {
    img {
      width: 100%;
    }
  }

  & > div {
    line-height: 140%;

    & > p {
      color: var(--c-nd-800);
      margin-bottom: 2em;
    }

    & > h4 {
      margin: 0 0 var(--unit) 0;
    }
  }
`;

const Featured = styled(LabelS).attrs({ bold: true })`
  display: inline-block;
  position: absolute;
  top: var(--unit);
  right: var(--unit);
  border-radius: var(--unit);
  padding: var(--half-unit) var(--unit);
  background-color: var(--c-blue-400);
`;

const DEFAULT_POST_PICTURE =
  'https://frontend-assets.sorare.com/meta/social-picture-2022.png';

export const Excerpt = ({ excerpt, featured, category }: Props) => {
  const postImage = usePostImage(excerpt, featured);

  return (
    <Container
      to={
        category
          ? generatePath(BLOG_POST, {
              sport: category,
              slug: excerpt.slug,
            })
          : generatePath('/blog/:slug', {
              slug: excerpt.slug,
            })
      }
    >
      {excerpt.featured?.featured === 'true' && <Featured>Featured</Featured>}
      {postImage && (
        <figure>
          <img
            src={postImage.url || DEFAULT_POST_PICTURE}
            alt={postImage.alt || excerpt.title || ''}
          />
        </figure>
      )}
      <div>
        <HeadlineM as="h4">{excerpt.title}</HeadlineM>
        <BodyM
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(excerpt.excerpt!),
          }}
        />
      </div>
    </Container>
  );
};

Excerpt.fragment = excerptFragment;
